import { Box, keyframes, styled } from '@mui/material';
import React, { useEffect, useState } from 'react';

// Animation keyframes
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.8; }
`;

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const drift = keyframes`
  0% { transform: translateX(0px); }
  50% { transform: translateX(20px); }
  100% { transform: translateX(0px); }
`;

const blink = keyframes`
  0%, 50% { opacity: 0.4; }
  51%, 100% { opacity: 0.8; }
`;

const slideIn = keyframes`
  0% { transform: translateX(-100px); opacity: 0; }
  50% { opacity: 0.6; }
  100% { transform: translateX(0px); opacity: 0.4; }
`;

const matrix = keyframes`
  0% { transform: translateY(-100px); opacity: 0; }
  50% { opacity: 0.7; }
  100% { transform: translateY(100vh); opacity: 0; }
`;

// Styled components
const DoodleContainer = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  pointerEvents: 'none',
  zIndex: 0,
});

const DoodleElement = styled(Box)<{ 
  top: string; 
  left: string; 
  delay: string; 
  duration: string;
  animationType: string;
}>(({ top, left, delay, duration, animationType, theme }) => ({
  position: 'absolute',
  top,
  left,
  color: theme.palette.primary.main,
  opacity: 0.15,
  fontSize: '1.5rem',
  fontFamily: 'monospace',
  fontWeight: 'bold',
  animationDelay: delay,
  animationDuration: duration,
  animationIterationCount: 'infinite',
  animationTimingFunction: 'ease-in-out',
  animationName: animationType === 'float' ? float : 
                 animationType === 'pulse' ? pulse :
                 animationType === 'rotate' ? rotate :
                 animationType === 'drift' ? drift :
                 animationType === 'slideIn' ? slideIn :
                 animationType === 'matrix' ? matrix :
                 blink,
  [theme.breakpoints.down('md')]: {
    fontSize: '1.2rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

const BinaryElement = styled(DoodleElement)(({ theme }) => ({
  fontSize: '0.9rem',
  color: theme.palette.text.secondary,
  opacity: 0.1,
  fontFamily: 'monospace',
  letterSpacing: '2px',
}));

const CodeSnippet = styled(DoodleElement)(({ theme }) => ({
  fontSize: '0.8rem',
  color: theme.palette.primary.light,
  opacity: 0.08,
  fontFamily: 'monospace',
  whiteSpace: 'pre-line',
  lineHeight: 1.2,
  maxWidth: '200px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    maxWidth: '150px',
  },
}));

const CircuitElement = styled(Box)<{ 
  top: string; 
  left: string; 
  delay: string; 
}>(({ top, left, delay, theme }) => ({
  position: 'absolute',
  top,
  left,
  width: '60px',
  height: '60px',
  opacity: 0.08,
  animationDelay: delay,
  animationDuration: '8s',
  animationIterationCount: 'infinite',
  animationTimingFunction: 'ease-in-out',
  animationName: pulse,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: 0,
    width: '100%',
    height: '2px',
    background: theme.palette.primary.main,
    transform: 'translateY(-50%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '50%',
    width: '2px',
    height: '100%',
    background: theme.palette.primary.main,
    transform: 'translateX(-50%)',
  },
  [theme.breakpoints.down('md')]: {
    width: '40px',
    height: '40px',
  },
}));

const NetworkNode = styled(Box)<{ 
  top: string; 
  left: string; 
  delay: string; 
}>(({ top, left, delay, theme }) => ({
  position: 'absolute',
  top,
  left,
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: theme.palette.primary.main,
  opacity: 0.2,
  animationDelay: delay,
  animationDuration: '4s',
  animationIterationCount: 'infinite',
  animationTimingFunction: 'ease-in-out',
  animationName: pulse,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '60px',
    height: '1px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, transparent)`,
    transform: 'translate(-50%, -50%)',
    opacity: 0.15,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '1px',
    height: '60px',
    background: `linear-gradient(180deg, ${theme.palette.primary.main}, transparent)`,
    transform: 'translate(-50%, -50%)',
    opacity: 0.15,
  },
}));

const GeometricShape = styled(Box)<{ 
  top: string; 
  left: string; 
  delay: string;
  shapeType: string;
}>(({ top, left, delay, shapeType, theme }) => ({
  position: 'absolute',
  top,
  left,
  width: '40px',
  height: '40px',
  opacity: 0.06,
  animationDelay: delay,
  animationDuration: '6s',
  animationIterationCount: 'infinite',
  animationTimingFunction: 'ease-in-out',
  animationName: rotate,
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: shapeType === 'circle' ? '50%' : '0',
  transform: shapeType === 'diamond' ? 'rotate(45deg)' : 'none',
  '&::before': shapeType === 'triangle' ? {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 0,
    height: 0,
    borderLeft: '15px solid transparent',
    borderRight: '15px solid transparent',
    borderBottom: `25px solid ${theme.palette.primary.main}`,
    transform: 'translate(-50%, -50%)',
  } : {},
  [theme.breakpoints.down('md')]: {
    width: '30px',
    height: '30px',
  },
}));

const TechDoodles: React.FC = () => {
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Tech symbols and code elements
  const techSymbols = [
    '{ }', '< />', '[ ]', '( )', '&&', '||', '==', '!=', '=>', '++', '--',
    'fn', 'var', 'let', 'const', 'if', 'else', 'for', 'while', 'return',
    'API', 'JSON', 'HTTP', 'CSS', 'HTML', 'JS', 'TS', 'SQL', 'Git',
    'React', 'Node', 'Express', 'MongoDB', 'Firebase', 'AWS', 'Docker', 'CI/CD', 'DevOps',
    '⚡', '🔧', '⚙️', '🚀', '💻', '🌐', '⭐', '🔥', '💡', '🎯',
    '∞', '→', '←', '↑', '↓', '⟨⟩', '∴', '∵', '∀', '∃', '△', '▽', '◇', '◆'
  ];

  const binaryStrings = [
    '1010101', '1100110', '0011001', '1111000', '0000111',
    '1001100', '0110011', '1010010', '0101101', '1100011'
  ];

  const codeSnippets = [
    'const app = () => {\n  return <div/>;\n}',
    'function init() {\n  console.log("Hello");\n}',
    'SELECT * FROM users\nWHERE active = true;',
    'git commit -m\n"feat: new feature"',
    'npm install\n--save-dev',
    'docker run -p 3000:3000\nmy-app',
    'curl -X POST\n/api/users',
    'import React from "react";\nexport default App;'
  ];

  const generateRandomPosition = () => ({
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 5}s`,
    duration: `${3 + Math.random() * 5}s`,
  });

  const generateElements = (count: number) => {
    const elements = [];
    const animationTypes = ['float', 'pulse', 'rotate', 'drift', 'blink', 'slideIn', 'matrix'];
    const shapeTypes = ['circle', 'square', 'diamond', 'triangle'];
    
    for (let i = 0; i < count; i++) {
      const position = generateRandomPosition();
      const elementType = Math.random();
      
      if (elementType > 0.92) {
        // Network nodes (8%)
        elements.push(
          <NetworkNode
            key={`network-${i}`}
            top={position.top}
            left={position.left}
            delay={position.delay}
          />
        );
      } else if (elementType > 0.85) {
        // Circuit elements (7%)
        elements.push(
          <CircuitElement
            key={`circuit-${i}`}
            top={position.top}
            left={position.left}
            delay={position.delay}
          />
        );
      } else if (elementType > 0.78) {
        // Geometric shapes (7%)
        elements.push(
          <GeometricShape
            key={`shape-${i}`}
            top={position.top}
            left={position.left}
            delay={position.delay}
            shapeType={shapeTypes[Math.floor(Math.random() * shapeTypes.length)]}
          />
        );
      } else if (elementType > 0.70) {
        // Code snippets (8%)
        elements.push(
          <CodeSnippet
            key={`code-${i}`}
            top={position.top}
            left={position.left}
            delay={position.delay}
            duration={position.duration}
            animationType={animationTypes[Math.floor(Math.random() * animationTypes.length)]}
          >
            {codeSnippets[Math.floor(Math.random() * codeSnippets.length)]}
          </CodeSnippet>
        );
      } else if (elementType > 0.55) {
        // Binary strings (15%)
        elements.push(
          <BinaryElement
            key={`binary-${i}`}
            top={position.top}
            left={position.left}
            delay={position.delay}
            duration={position.duration}
            animationType={animationTypes[Math.floor(Math.random() * animationTypes.length)]}
          >
            {binaryStrings[Math.floor(Math.random() * binaryStrings.length)]}
          </BinaryElement>
        );
      } else {
        // Tech symbols (55%)
        elements.push(
          <DoodleElement
            key={`tech-${i}`}
            top={position.top}
            left={position.left}
            delay={position.delay}
            duration={position.duration}
            animationType={animationTypes[Math.floor(Math.random() * animationTypes.length)]}
          >
            {techSymbols[Math.floor(Math.random() * techSymbols.length)]}
          </DoodleElement>
        );
      }
    }
    
    return elements;
  };

  // Adjust number of elements based on screen size
  const getElementCount = () => {
    if (windowSize.width < 600) return 20;
    if (windowSize.width < 900) return 35;
    return 50;
  };

  return (
    <DoodleContainer>
      {generateElements(getElementCount())}
    </DoodleContainer>
  );
};

export default TechDoodles; 